/**
 * ReportControlBar — the single control row below the report editor. Replaces
 * the old two-row split (ReportStatusline + ReportToolbar).
 *
 * Layout (left → right):
 *   [Undo] [Redo]  [🎙 Dictate]      ·····      <autosave>  [↻ Impression]  [⚙]  [Sign]
 *
 * Common actions stay visible (dictate, regenerate impression, sign); rare /
 * advanced ones (AI mode, normal-field visibility, mic settings, reset draft)
 * live in the ReportSettingsMenu gear. Dictation is the radiologist's primary
 * input, so it gets a labeled, always-visible button — not just hold-backtick.
 */

import { FaMicrophone, FaRedo, FaSpinner, FaUndo } from "../../utils/icons";
import { ReportSettingsMenu } from "./ReportSettingsMenu";
import type { ReporterAiMode } from "../../atoms/reporterPrefsAtoms";

/**
 * Dictation phase shown on the Dictate button — the single dictation-status
 * home for the page (it replaced a separate fixed overlay). Derived in
 * PrototypeReport from the dictation stage + voice-adjust state. Capture
 * phases (listening/refining) pulse; processing phases (transcribing/
 * adjusting) show a spinner. "refining"/"adjusting" are the ctrl+` voice-adjust
 * path — distinct labels so the button never misreports a chip refine as
 * editor "Listening…". Strip-row dictation is a first-class editor-mic path,
 * so it reads "Listening…" like editor dictation.
 */
export type DictationPhase =
  | "connecting"
  | "idle"
  | "listening"
  | "refining"
  | "transcribing"
  | "adjusting";

const DICTATION_PHASE_LABEL: Record<DictationPhase, string> = {
  idle: "Dictate",
  connecting: "Connecting…",
  listening: "Listening…",
  refining: "Refining…",
  transcribing: "Transcribing…",
  adjusting: "Adjusting…",
};

interface ReportControlBarProps {
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;

  isSigned: boolean;
  editingLocked?: boolean;
  autoSaveStatus: string | null;
  hasReportData: boolean;

  // Dictation — visible push-to-talk (mirrors hold-backtick). The button is
  // the single dictation-status home: dictationPhase reflects every path
  // (editor, ctrl+` refine, strip-row), not just this button's own trigger.
  dictationActive: boolean;
  dictationPhase: DictationPhase;
  onToggleDictation: () => void;

  // Settings gear.
  reporterAiMode: ReporterAiMode;
  onChangeMode: (mode: ReporterAiMode) => void;
  showNormalFields: boolean;
  onToggleNormalFields: () => void;
  onClearDraft: () => void;
  onOpenMicSettings: () => void;
  canEditTemplates: boolean;

  // Sign / unsign (hidden in demo sessions).
  sessionMode: "demo" | "labeling-reporting";
  onSignReport: () => void;
  onUnsignReport?: () => void;
  isSigningPending: boolean;
  isUnsigningPending: boolean;
}

export function ReportControlBar({
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  isSigned,
  editingLocked = false,
  autoSaveStatus,
  hasReportData,
  dictationActive,
  dictationPhase,
  onToggleDictation,
  reporterAiMode,
  onChangeMode,
  showNormalFields,
  onToggleNormalFields,
  onClearDraft,
  onOpenMicSettings,
  canEditTemplates,
  sessionMode,
  onSignReport,
  onUnsignReport,
  isSigningPending,
  isUnsigningPending,
}: ReportControlBarProps) {
  const showPrimary = sessionMode !== "demo";

  return (
    <div className="report-toolbar report-control-bar">
      <div className="report-actions">
        <button
          type="button"
          className="report-history-btn"
          onClick={onUndo}
          disabled={!canUndo || isSigned || editingLocked}
          title="Undo (⌘Z)"
          aria-label="Undo"
        >
          <FaUndo className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          className="report-history-btn"
          onClick={onRedo}
          disabled={!canRedo || isSigned || editingLocked}
          title="Redo (⇧⌘Z)"
          aria-label="Redo"
        >
          <FaRedo className="w-3.5 h-3.5" />
        </button>

        {dictationActive && !isSigned && !editingLocked && (
          <button
            type="button"
            className={`report-mic-btn ${dictationPhase !== "idle" ? "is-recording" : ""}`}
            // Preserve the editor selection until the click snapshots its destination.
            onPointerDown={(event) => event.preventDefault()}
            onClick={onToggleDictation}
            disabled={["connecting", "transcribing", "adjusting"].includes(dictationPhase)}
            aria-pressed={dictationPhase !== "idle"}
            title="Dictate — tap to start/stop, or hold the ` key"
          >
            {dictationPhase === "listening" || dictationPhase === "refining" ? (
              <span className="report-mic-btn__dot" aria-hidden="true" />
            ) : ["connecting", "transcribing", "adjusting"].includes(dictationPhase) ? (
              <FaSpinner className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
            ) : (
              <FaMicrophone className="w-3.5 h-3.5" />
            )}
            <span>{DICTATION_PHASE_LABEL[dictationPhase]}</span>
            {dictationPhase === "idle" && <span className="report-mic-btn__key">`</span>}
          </button>
        )}
      </div>

      <div className="report-actions">
        {autoSaveStatus && (
          <span data-testid="autosave-status" className="report-statusline__item">
            {autoSaveStatus}
          </span>
        )}

        {!isSigned && !editingLocked && (
          <ReportSettingsMenu
            reporterAiMode={reporterAiMode}
            onChangeMode={onChangeMode}
            showNormalFields={showNormalFields}
            onToggleNormalFields={onToggleNormalFields}
            onClearDraft={onClearDraft}
            onOpenMicSettings={onOpenMicSettings}
            hasReportData={hasReportData}
            dictationActive={dictationActive}
            canEditTemplates={canEditTemplates}
          />
        )}

        {showPrimary &&
          (isSigned ? (
            onUnsignReport ? (
              <button
                type="button"
                onClick={onUnsignReport}
                disabled={isUnsigningPending}
                className="report-btn report-btn--danger"
                aria-label="Unsign this report"
              >
                {isUnsigningPending ? "Unsigning..." : "Unsign Report"}
              </button>
            ) : (
              <span>Signed</span>
            )
          ) : (
            <button
              type="button"
              onClick={onSignReport}
              disabled={isSigningPending}
              className="report-btn report-btn--primary"
              aria-label="Sign report"
            >
              {isSigningPending ? "Signing..." : "Sign Report"}
            </button>
          ))}
      </div>
    </div>
  );
}
